import { Aperture, ArrowUp, Battery, CircleUserRound, MapPin, Settings2, ShoppingBag, Signal, Wifi, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { GuideSyncControl } from './GuideSyncControl'
import { guideDisplayKey, guideDisplaySchema, type GuideDisplaySettings } from '../lib/guide-display-settings'

export function GuideStatusBar({ settings, editable, edit }: { settings: GuideDisplaySettings; editable: boolean; edit: () => void }) {
  return <div className="guide-status-bar" aria-label={`展示时间 ${settings.time}，电量 ${settings.battery}%`}>
    <span className="guide-status-time">{settings.time}</span>
    <div className="guide-status-icons" aria-hidden="true"><Signal /><Wifi /><span className="guide-battery"><Battery /><b>{settings.battery}</b></span></div>
    {editable && <button className="guide-status-edit" onClick={edit} aria-label="修改状态栏和屏幕宽度" title="修改时间、电量和屏幕宽度"><Settings2 size={14}/> 修改状态栏</button>}
  </div>
}

export function GuideBottomBar() {
  return <footer className="guide-bottom-bar" aria-label="展示页面底栏">
    <div className="guide-bottom-items">
      <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><ArrowUp/><span>回顶部</span></button>
      <span className="guide-bottom-display"><MapPin/><span>附近</span></span>
      <span className="guide-bottom-display"><ShoppingBag/><span>红包</span></span>
      <span className="guide-bottom-display"><Aperture/><span>发圈</span></span>
      <span className="guide-bottom-display"><CircleUserRound/><span>我的</span></span>
    </div>
    <div className="guide-home-area" aria-hidden="true"><span/></div>
  </footer>
}

export function GuideDisplayEditor({ settings, close, saved, productsUpdated }: { settings: GuideDisplaySettings; close: () => void; saved: (value: GuideDisplaySettings) => void; productsUpdated: () => void }) {
  const [draft, setDraft] = useState(settings)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  const save = async () => {
    const parsed = guideDisplaySchema.safeParse(draft)
    if (!parsed.success) { setError('时间请用 00:00–23:59，电量请填 0–100 的整数。'); return }
    setBusy(true); setError('')
    try { await api.saveSetting(guideDisplayKey, JSON.stringify(parsed.data)); saved(parsed.data) }
    catch (reason) { setError(reason instanceof Error ? reason.message : '保存失败，请重试'); setBusy(false) }
  }
  return <dialog className="guide-display-editor" ref={dialog} aria-labelledby="guide-display-title" onCancel={event => { event.preventDefault(); if (!busy) close() }}>
    <form onSubmit={event => { event.preventDefault(); void save() }}>
      <header><h2 id="guide-display-title">状态栏与屏幕</h2><button type="button" onClick={close} disabled={busy} aria-label="关闭状态栏设置"><X size={20}/></button></header>
      <p>用于展示和截图，不代表设备真实状态；保存后所有同事都能看到。</p>
      <label>显示时间<input type="text" required placeholder="10:55" maxLength={5} value={draft.time} onChange={e => setDraft(current => ({ ...current, time: e.target.value }))}/></label>
      <label>电量百分比<input type="number" required min="0" max="100" step="1" value={Number.isNaN(draft.battery) ? '' : draft.battery} onChange={e => setDraft(current => ({ ...current, battery: e.target.value === '' ? Number.NaN : Number(e.target.value) }))}/></label>
      <label>桌面展示宽度<select value={draft.width} onChange={e => setDraft({ ...draft, width: Number(e.target.value) as 393 | 430 })}><option value={393}>393px · 标准屏</option><option value={430}>430px · 大屏</option></select></label>
      <GuideSyncControl updated={productsUpdated}/>
      {error && <p className="guide-display-error" role="alert">{error}</p>}
      <footer><button type="button" onClick={close} disabled={busy}>取消</button><button type="submit" disabled={busy}>{busy ? '保存中…' : '保存并立即显示'}</button></footer>
    </form>
  </dialog>
}
